/**
 * Viator interest taxonomy loader + selection helpers (iOS ViatorInterestTaxonomy parity).
 */

import taxonomyJson from '../data/viator_interest_taxonomy_60.json';
import {
  ACTIVITY_INTEREST_MAX,
  ACTIVITY_INTEREST_MIN,
} from './travelerPreferenceOptions';

export interface ViatorInterest {
  id: string;
  name: string;
  description: string;
}

export interface ViatorInterestGroup {
  id: string;
  name: string;
  description: string;
  children: ViatorInterest[];
}

export interface ViatorInterestTaxonomy {
  schemaVersion: string;
  categoryCount: number;
  groupCount: number;
  groups: ViatorInterestGroup[];
}

let cached: ViatorInterestTaxonomy | null = null;

export function loadViatorInterestTaxonomy(): ViatorInterestTaxonomy {
  if (cached) return cached;
  cached = taxonomyJson as ViatorInterestTaxonomy;
  return cached;
}

export function loadViatorInterestGroups(): ViatorInterestGroup[] {
  return loadViatorInterestTaxonomy().groups;
}

export type ActivityInterestGroupSelectionState =
  | 'none'
  | 'allIncluded'
  | { partial: number };

export function activityInterestSelectionState(
  group: ViatorInterestGroup,
  selection: ReadonlySet<string>
): ActivityInterestGroupSelectionState {
  if (selection.has(group.id)) return 'allIncluded';
  const count = group.children.filter((c) => selection.has(c.id)).length;
  if (count > 0) return { partial: count };
  return 'none';
}

/** All-included group counts as 1; each child counts separately. */
export function effectiveActivityInterestCount(
  selection: ReadonlySet<string>,
  groups: ViatorInterestGroup[] = loadViatorInterestGroups()
): number {
  let count = 0;
  for (const group of groups) {
    if (selection.has(group.id)) {
      count += 1;
    } else {
      count += group.children.filter((c) => selection.has(c.id)).length;
    }
  }
  return count;
}

export function toggleActivityInterestGroup(
  group: ViatorInterestGroup,
  selection: ReadonlySet<string>
): Set<string> {
  const next = new Set(selection);
  if (next.has(group.id)) {
    next.delete(group.id);
    return next;
  }
  const effective = effectiveActivityInterestCount(next);
  if (effective >= ACTIVITY_INTEREST_MAX) return next;
  for (const child of group.children) next.delete(child.id);
  next.add(group.id);
  return next;
}

export function toggleActivityInterestChild(
  interestId: string,
  group: ViatorInterestGroup,
  selection: ReadonlySet<string>
): Set<string> {
  const next = new Set(selection);
  if (next.has(interestId)) {
    next.delete(interestId);
    return next;
  }
  if (next.has(group.id)) {
    next.delete(group.id);
    for (const child of group.children) {
      if (child.id !== interestId) next.add(child.id);
    }
    return next;
  }
  const effective = effectiveActivityInterestCount(next);
  if (effective >= ACTIVITY_INTEREST_MAX) return next;
  next.add(interestId);
  return next;
}

export { ACTIVITY_INTEREST_MIN, ACTIVITY_INTEREST_MAX };
